/**
 * A new agent (design 11 §3.1): from a template (pick one, name it), or described in words and drafted by the
 * copilot (V1-E09); then opened in the builder.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { PhoneIncoming, PhoneOutgoing, X } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { ApiError, data, type Schemas } from "../api/client";
import { useApiQuery } from "../app/Page";
import { useBff, useCan } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState, Loading } from "../ui/states";
import { DescribeAgent } from "./DescribeAgent";

type Template = Schemas["TemplateEntry"];
type Kit = Schemas["ProviderKit"];
const ROLES = ["stt", "llm", "tts"] as const;
type Role = { provider?: string; credential?: string };

/** The template's own providers and the keys they name, as the builder would show them. */
function cascadeOf(template: Template): { providers: string[]; credentials: string[] } {
  const spec = (template.agent as { spec?: Partial<Record<(typeof ROLES)[number], Role>> }).spec ?? {};
  const roles = ROLES.map((r) => spec[r]).filter((r): r is Role => Boolean(r));
  return {
    providers: [...new Set(roles.map((r) => r.provider ?? ""))].filter(Boolean),
    credentials: [...new Set(roles.map((r) => r.credential ?? ""))].filter(Boolean),
  };
}
const NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export function NewAgentDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useT();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-12 max-h-[85vh] w-full max-w-2xl -translate-x-1/2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">{t("agents.new.title")}</Dialog.Title>
              <Dialog.Description className="text-sm text-[var(--color-muted)]">{t("agents.new.intro")}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" aria-label={t("common.close")}>
                <X size={16} aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </div>
          {open && <HowToStart onDone={() => onOpenChange(false)} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function HowToStart({ onDone }: { onDone: () => void }) {
  const t = useT();
  const tenant = useApiQuery("tenant", (a) => a.GET("/v1/tenant"));
  const [chosen, setHow] = useState<"template" | "describe" | null>(null);
  // a copilot model set: describing the agent is the quickest start (V1.1-EA-T03); else the templates
  const how = chosen ?? (tenant.data?.copilot ? "describe" : "template");
  return (
    <div className="space-y-4">
      {tenant.data && !tenant.data.copilot && <CopilotOffer onSet={() => setHow("describe")} />}
      <div role="group" aria-label={t("agents.new.how")} className="inline-flex rounded-md border border-[var(--color-border)] p-0.5">
        {(["template", "describe"] as const).map((h) => (
          <button key={h} type="button" aria-pressed={how === h} onClick={() => setHow(h)}
            className="rounded px-3 py-1 text-sm aria-pressed:bg-[var(--color-surface)] aria-pressed:font-semibold">
            {t(h === "template" ? "agents.new.fromTemplate" : "agents.new.describe")}
          </button>
        ))}
      </div>
      {how === "template" ? <NewAgentForm onDone={onDone} /> : <DescribeAgent onDone={onDone} />}
    </div>
  );
}

function NewAgentForm({ onDone }: { onDone: () => void }) {
  const t = useT();
  const { api } = useBff();
  const navigate = useNavigate();
  const queries = useQueryClient();
  const nameId = useId();
  const tenant = useApiQuery("tenant", (a) => a.GET("/v1/tenant"));
  const country = tenant.data?.country ?? undefined;
  const templates = useApiQuery(
    `agent-templates:${country ?? "any"}`,
    (a) => a.GET("/v1/agent-templates", { params: { query: country ? { country } : {} } }),
    { enabled: !tenant.isPending }, // the tenant's country first: its templates, and those for anywhere
  );
  const [chosen, setChosen] = useState<Template | null>(null);
  const [name, setName] = useState("");
  const [kit, setKit] = useState<string>(""); // "": the template's own providers; "default": the tenant's (V1.2-UX-T05)
  const defaults = tenant.data?.default_models ?? null;

  const create = useMutation({
    mutationFn: async () => {
      if (!chosen) throw new Error("no template");
      // with a kit, the server swaps in its providers, pointed at the tenant's own key for them
      const query = kit === "default" ? { defaults: true } : { kit };
      const source = kit
        ? await data(api.GET("/v1/agent-templates/{template_id}", { params: { path: { template_id: chosen.id }, query } }))
        : chosen;
      const doc = source.agent as Schemas["AgentDocument"]; // the server validated it when it loaded the template
      return data(api.POST("/v1/agents", { body: { ...doc, metadata: { ...doc.metadata, name } } }));
    },
    onSuccess: async (agent) => {
      await queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("agents") });
      onDone();
      await navigate({ to: "/agents/$agentId", params: { agentId: agent.id }, search: { created: true } });
    },
  });

  const choose = (template: Template) => {
    setChosen(template);
    setName(template.id);
    setKit(defaults ? "default" : (template.suggested_kit ?? ""));
    create.reset();
  };
  const kits: Kit[] = templates.data?.kits ?? [];
  const nameProblem = name && !NAME.test(name) ? t("agents.new.nameRule") : null;
  const conflict = create.error instanceof ApiError && create.error.status === 409;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (chosen && name && !nameProblem) create.mutate();
  };

  if (templates.isPending || tenant.isPending) return <Loading label={t("app.loading")} />;
  if (templates.isError) return <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />;
  return (
    <form onSubmit={submit} className="space-y-5">
      <fieldset>
        <legend className="mb-2 text-sm font-medium">{t("agents.new.template")}</legend>
        <div role="radiogroup" aria-label={t("agents.new.template")} className="grid gap-3 sm:grid-cols-2">
          {templates.data.data.map((template) => {
            const selected = chosen?.id === template.id;
            const Icon = template.direction === "outbound" ? PhoneOutgoing : PhoneIncoming;
            return (
              <button
                key={template.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => choose(template)}
                className={`rounded-lg border p-4 text-left hover:bg-[var(--color-surface)] focus-visible:outline-2 focus-visible:outline-[var(--color-primary)] ${
                  selected ? "border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]" : "border-[var(--color-border)]"
                }`}
              >
                <span className="flex items-center gap-2 font-medium">
                  <Icon size={16} aria-hidden="true" />
                  {template.title}
                </span>
                <span className="mt-1 block text-sm text-[var(--color-muted)]">{template.description}</span>
                <span className="mt-2 flex gap-2 text-xs text-[var(--color-muted)]">
                  <span>{t(`agents.direction.${template.direction}`)}</span>
                  {(template.countries ?? []).length > 0 && <span>· {(template.countries ?? []).join(", ")}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>
      {chosen && (
        <div className="space-y-1">
          <label htmlFor={nameId} className="block text-sm font-medium">
            {t("agents.new.name")}
          </label>
          <input
            id={nameId}
            value={name}
            onChange={(e) => {
              setName(e.target.value.toLowerCase());
              create.reset();
            }}
            aria-invalid={Boolean(nameProblem || conflict)}
            aria-describedby={`${nameId}-hint`}
            className="w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-1.5 font-mono"
            autoComplete="off"
            spellCheck={false}
          />
          <p id={`${nameId}-hint`} className={`text-sm ${nameProblem || conflict ? "text-[var(--color-danger)]" : "text-[var(--color-muted)]"}`}>
            {conflict ? t("agents.new.nameTaken", { name }) : (nameProblem ?? t("agents.new.nameHint"))}
          </p>
        </div>
      )}
      {chosen && ((chosen.kits ?? []).length > 0 || defaults) && (
        <Providers template={chosen} kits={kits.filter((k) => (chosen.kits ?? []).includes(k.id))} value={kit} defaults={defaults}
          onChange={(next) => { setKit(next); create.reset(); }} />
      )}
      {create.isError && !conflict && <ErrorState error={create.error} />}
      <div className="flex justify-end gap-2">
        <Dialog.Close asChild>
          <Button variant="secondary">{t("common.cancel")}</Button>
        </Dialog.Close>
        <Button type="submit" disabled={!chosen || !name || Boolean(nameProblem) || create.isPending}>
          {t(create.isPending ? "agents.new.creating" : "agents.new.create")}
        </Button>
      </div>
    </form>
  );
}

/** Where the agent's speech and model come from: the template's own providers (a key each), or a one-key kit. */
function Providers({ template, kits, value, defaults, onChange }: {
  template: Template;
  kits: Kit[];
  /** The tenant's default models (V1.2-UX-T05): offered first when it has them. */
  defaults: Schemas["DefaultModels"] | null;
  value: string;
  onChange: (kit: string) => void;
}) {
  const t = useT();
  const own = cascadeOf(template);
  const option = (id: string, label: string, hint: string, suggested: boolean) => (
    <label key={id || "template"} className="flex items-start gap-2 text-sm">
      <input type="radio" name="kit" value={id} checked={value === id} onChange={() => onChange(id)} className="mt-1" />
      <span>
        <span className="font-medium">{label}</span>
        {suggested && <span className="ml-2 rounded bg-[var(--color-surface)] px-1.5 text-xs">{t("agents.new.kitSuggested")}</span>}
        <span className="block text-[var(--color-muted)]">{hint}</span>
      </span>
    </label>
  );
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm font-medium">{t("agents.new.providers")}</legend>
      {defaults && option("default", t("agents.new.defaults"),
        t("agents.new.defaultsHint", { providers: [...new Set([defaults.stt, defaults.llm, defaults.tts].map((r) => String(r.provider)))].join(", ") }),
        false)}
      {option("", t("agents.new.asTemplate", { providers: own.providers.join(", ") }),
        t("agents.new.asTemplateHint", { credentials: own.credentials.join(", ") }), false)}
      {kits.map((k) => option(k.id, t("agents.new.kit", { label: k.label }), k.description, template.suggested_kit === k.id))}
    </fieldset>
  );
}

/** No copilot model yet, and the tenant has an OpenAI or Groq key: one click makes it the copilot's, so the agent
 * can be described in words. Only for someone who may change settings and see credentials. */
function CopilotOffer({ onSet }: { onSet: () => void }) {
  const settings = useCan("settings:write");
  const credentials = useCan("credentials:write");
  return settings && credentials ? <CopilotFromKey onSet={onSet} /> : null;
}

const COPILOT_PROVIDERS: Record<string, string> = { openai: "OpenAI", groq: "Groq" };

function CopilotFromKey({ onSet }: { onSet: () => void }) {
  const t = useT();
  const { api } = useBff();
  const queries = useQueryClient();
  const credentials = useApiQuery("credentials:copilot", (a) => a.GET("/v1/credentials", { params: { query: { limit: 100 } } }));
  const usable = (credentials.data?.data ?? []).find((c) => c.provider in COPILOT_PROVIDERS);
  const set = useMutation({
    mutationFn: () => data(api.PATCH("/v1/tenant", {
      body: { copilot: { model: { provider: usable?.provider as "openai" | "groq", credential: usable?.name } } },
    })),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ["tenant"] });
      onSet();
    },
  });
  if (!usable) return null;
  const provider = COPILOT_PROVIDERS[usable.provider] ?? usable.provider;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm">
      <span className="flex-1">{t("agents.new.copilotOffer", { provider })}</span>
      <Button variant="secondary" disabled={set.isPending} onClick={() => set.mutate()}>
        {t("agents.new.copilotUse", { provider, name: usable.name })}
      </Button>
      {set.isError && <ErrorState error={set.error} />}
    </div>
  );
}
